import { CheckOutlined, CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { Col, Divider, Row, Select, Switch, Table, Typography } from "antd";
import { cloneDeep, get, isEmpty, toLower } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../../components/functional_components/Button/PFButton";
import DetailBox from "../../../components/functional_components/DetailBox/DetailBox";
import { setLoading } from "../../../redux/ducks/Loading";
import { setSection } from "../../../redux/ducks/Section";
import Project from "../../../services/api/Project";
import ClientPortal from "../../../services/api/ClientPortal";
import ProjectEstimate from "../../../services/api/ProjectEstimate";
import { dbDate, langDate, paginationDefaults } from "../../../utils/properties";
import { buildQueryString, showError, showSuccess, trimData } from "../../../utils/utilities";
import SaleOrder from "../../../services/api/SaleOrder";
import ProjectEstimateStatus from "../../../services/api/ProjectEstimateStatus";
import { useFormik } from "formik";
import ProjectTask from "../../../services/api/ProjectTask";
import Wbs from "../../../services/api/Wbs";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;

const ProjectsDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const [state, setState] = useState({
    dataContent: {},
    tableContent: [],
    expandedTableContent: [],
    expandedRowKeys: [],
    loading: false,
    supportPm: [],
    preventive: [],
    preventiveClient: [],
    projectMaster: [],
    salesOrder: [],
    statusSelling: [],
    wbs: [],
    wbsForm: {},
    wbsFormDirty: false,
    pagination: {
      currentPage: paginationDefaults.page,
      pageSize: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
  });
  const contractType = [
    {
      value: t("projects.detail.planning"),
      id: "PLANNING",
    },
    {
      value: t("projects.detail.royalties"),
      id: "ROYALTIES",
    },
    {
      value: t("projects.detail.windTunnel"),
      id: "WIND_TUNNEL",
    },
    {
      value: t("projects.detail.otherIncome"),
      id: "OTHER_INCOME",
    },
  ];
  const incomeType = [
    {
      value: t("projects.detail.poc"),
      id: "POC",
    },
    {
      value: t("projects.detail.billed"),
      id: "BILLED",
    },
  ];
  const paymentTerms = ["0", "30", "60", "90", "120", "180"];

  const evaluationCriteria = [
    {
      value: t("projects.detail.completionPercentage"),
      id: "COMPLETION_PERCENTAGE",
    },
    {
      value: t("projects.detail.revenue"),
      id: "REVENUE",
    },
    {
      value: t("projects.detail.CostCoverage"),
      id: "COST_COVERAGE",
    },
    {
      value: t("projects.detail.CostMarginSale"),
      id: "COST_MARGIN_SALE",
    },
    {
      value: t("projects.detail.other"),
      id: "OTHER",
    },
  ];

  useEffect(() => {
    if (isEmpty(state.dataContent)) {
      dataGet();
      getTableContent();
    } else {
      getSupportPm();
      getPreventiveList();
      getPreventiveClientList();
      getProjectMaster();
      getSalesOrders();
      getStatusSelling();
      dispatch(
        setSection({
          section: {
            title: `${state.dataContent.id} | ${state.dataContent.name}`,
          },
        })
      );
    }
  }, [id, state.dataContent]);

  const getSupportPm = async () => {
    const listResponse = await Project.listSupportPm();
    if (!listResponse.error) {
      setState((prevState) => ({
        ...prevState,
        supportPm: get(listResponse, "data", []),
      }));
    } else {
      showError(get(listResponse, "error.messageCode", "ER000"));
    }
  };

  const getPreventiveList = async () => {
    const queryS = {
      allPages: true,
      associated: false,
      includeProjectEstimateIds: [state.dataContent.projectEstimateId],
    };
    const queryString = buildQueryString({}, queryS);
    const projectEstimateList = await ProjectEstimate.projectEstimateList(queryString.string);
    if (!projectEstimateList.error) {
      setState((prevState) => ({
        ...prevState,
        preventive: get(projectEstimateList, "data", []),
      }));
    } else {
      showError(get(projectEstimateList, "error.messageCode", "ER000"));
    }
  };
  const getPreventiveClientList = async () => {
    const preventiveClient = await ClientPortal.getListClient("allPages=true");
    if (!preventiveClient.error) {
      setState((prevState) => ({
        ...prevState,
        preventiveClient: get(preventiveClient, "data", []),
      }));
    } else {
      showError(get(preventiveClient, "error.messageCode", "ER000"));
    }
    return preventiveClient;
  };

  const getProjectMaster = async () => {
    const projectMaster = await Project.listProject("allPages=true");
    if (!projectMaster.error) {
      setState((prevState) => ({
        ...prevState,
        projectMaster: get(projectMaster, "data", []),
      }));
    }
    return projectMaster;
  };

  const getSalesOrders = async () => {
    const salesOrder = await SaleOrder.getSaleOrders("allPages=true");
    if (!salesOrder.error) {
      setState((prevState) => ({
        ...prevState,
        salesOrder: get(salesOrder, "data", []),
      }));
    } else {
      showError(get(salesOrder, "error.messageCode", "ER000"));
    }
  };

  const getStatusSelling = async () => {
    const statuses = await ProjectEstimateStatus.getStatusesList("");
    if (!statuses.error) {
      setState((prevState) => ({
        ...prevState,
        statusSelling: get(statuses, "data", []),
      }));
    } else {
      showError(get(statuses, "error.messageCode", "ER000"));
    }
  };

  const dataGet = async () => {
    dispatch(setLoading(true));
    const project = await Project.getProject(id);
    if (!project.error) {
      if (project.statusCode) {
        navigate("/projects");
      } else if (project.data) {
        setState((prevState) => ({
          ...prevState,
          dataContent: get(project, "data", {}),
        }));
      }
    } else {
      showError(get(project, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const formikProject = useFormik({
    initialValues: {
      support_pm: get(state.dataContent, "userSupportPm.cid", null),
      preventive: get(state.dataContent, "projectEstimateId", null),
      preventiveClient: get(state.dataContent, "clientPortalId", null),
      projectMaster: get(state.dataContent, "projectPrincipalId", null),
      salesOrder: get(state.dataContent, "saleOrderId", null),
      statusSelling: get(state.dataContent, "projectEstimateStatusId", null),
      contractType: get(state.dataContent, "contractType", null),
      incomeType: get(state.dataContent, "incomeType", null),
      paymentTerms: get(state.dataContent, "paymentTerms", null),
      evaluationCriteria: get(state.dataContent, "evaluationCriteria", null),
      vat: get(state.dataContent, "vat", false),
      vht: get(state.dataContent, "vht", false),
      enabled: get(state.dataContent, "enabled", false),
      exportBpc: get(state.dataContent, "exportBpc", false),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.support_pm) {
        errors.support_pm = "Required!";
      }
      if (!values.projectMaster) {
        errors.projectMaster = "Required!";
      }

      return errors;
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      const saveData = state.dataContent;
      let supportPmToSave = null;
      state.supportPm.forEach((sp) => {
        if (values.support_pm === sp.cid) {
          supportPmToSave = sp;
        }
      });
      saveData.clientPortalId = values.preventiveClient;
      saveData.projectEstimateId = values.preventive;
      saveData.projectPrincipalId = values.projectMaster;
      saveData.saleOrderId = values.salesOrder;
      saveData.projectEstimateStatusId = values.statusSelling;
      saveData.userSupportPm = supportPmToSave;
      saveData.contractType = values.contractType;
      saveData.incomeType = values.incomeType;
      saveData.paymentTerms = values.paymentTerms;
      saveData.evaluationCriteria = values.evaluationCriteria;
      saveData.vat = values.vat;
      saveData.vht = values.vht;
      saveData.enabled = values.enabled !== null ? values.enabled : true;
      saveData.exportBpc = values.exportBpc;
      saveData.projectTaskWbs = state.wbsForm;
      submitPm(saveData);
    },
  });

  const submitPm = async (data) => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const saveResponse = await Project.saveProject(data);
    if (!saveResponse.error) {
      showSuccess();
      navigate("/projects");
    } else {
      showError(get(saveResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const getTableContent = async () => {
    let wbsForm = {};
    const projectTasksAndPhases = await ProjectTask.getProjectTasksAndPhasesOfProject(
      `id=${id}&sort=id;desc&allPages=true`
    );
    if (!projectTasksAndPhases.error && projectTasksAndPhases.data) {
      projectTasksAndPhases.data.forEach((item, i) => {
        item.key = i; // This is necessary to remove warning in console for unique key row
        wbsForm[item.id] = item.wbsId !== null ? item.wbsId : "";
      });
      const wbsList = await Wbs.getWbsStructureList("allPages=true&active=true");
      if (wbsList.error) {
        showError(get(projectTasksAndPhases, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({
        ...prevState,
        tableContent: projectTasksAndPhases.data,
        wbs: get(wbsList, "data", []),
        wbsForm,
        pagination: {
          currentPage: projectTasksAndPhases.currentPage
            ? projectTasksAndPhases.currentPage + 1
            : paginationDefaults.page,
          pageSize: get(projectTasksAndPhases, "pageSize", paginationDefaults.size),
          totalItems: get(projectTasksAndPhases, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(projectTasksAndPhases, "error.messageCode", "ER000"));
    }
  };

  const changeWbsSelect = (value, item) => {
    let changeForm = cloneDeep(state.wbsForm);
    changeForm[item.id] = value;
    setState((prevState) => ({ ...prevState, wbsForm: changeForm, wbsFormDirty: true }));
  };

  const checkWbsForm = () => {
    let ck = true;
    Object.keys(state.wbsForm).forEach((v) => {
      if (state.wbsForm[v] === "") {
        ck = false;
      }
    });
    return ck;
  };

  const getCostCenterLabel = () => {
    if (!isEmpty(state.dataContent.costCentre)) {
      const costCenterName =
        state.dataContent.costCentre.name !== null ? ` | ${state.dataContent.costCentre.name}` : "";
      return `${state.dataContent.costCentre.id} ${costCenterName}`;
    } else {
      return "-";
    }
  };

  const columns = [
    {
      title: t("projects.detail.projectTask"),
      dataIndex: "id",
      key: "id",
      render: (value, row) => `${value} | ${row.name}`,
    },
    {
      title: t("projects.detail.projectPhase"),
      dataIndex: "projectPhase",
      key: "projectPhase",
      render: (value) => `${value.id} | ${value.name}`,
      responsive: ["lg"],
    },
    {
      title: t("projects.detail.pojectTaskStatus"),
      dataIndex: "status",
      key: "status",
    },
    {
      title: t("projects.detail.timeRegistration"),
      dataIndex: "timeRegistration",
      key: "timeRegistration",
      render: (value) =>
        value ? <CheckOutlined className="icon-medium" /> : <CloseOutlined className="icon-medium" />,
    },
    {
      title: `${t("projects.detail.wbs")}*`,
      dataIndex: "wbsId",
      key: "wbs",
      render: (_, item) => (
        <Select
          showSearch
          placeholder={t("projects.detail.wbs")}
          id="wbs"
          name="wbs"
          className="w-100"
          onChange={(e) => changeWbsSelect(e, item)}
          value={state.wbsForm[item.id] !== "" ? state.wbsForm[item.id] : null}
        >
          {state.wbs.map((item) => (
            <Option key={item.id} value={item.id} name={item.name}>
              {item.id} | {item.name}
            </Option>
          ))}
        </Select>
      ),
    },
  ];

  return (
    <DetailBox title={`${state.dataContent.id} | ${state.dataContent.name}`}>
      <div className="padding-30">
        <Row gutter={[48, 32]}>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.id")}</label>
            </Row>
            <Row>
              <p>{!isEmpty(state.dataContent.id) ? state.dataContent.id : "-"}</p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.society")}</label>
            </Row>
            <Row>
              <p>{!isEmpty(state.dataContent.society) ? state.dataContent.society : "-"}</p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.costCentre")}</label>
            </Row>
            <Row>
              <p>{getCostCenterLabel()}</p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.projectDescription")}</label>
            </Row>
            <Row>
              <p>{!isEmpty(state.dataContent.name) ? state.dataContent.name : "-"}</p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.company")}</label>
            </Row>
            <Row>
              <p>{!isEmpty(state.dataContent.company) ? state.dataContent.company : "-"}</p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.userPm")}</label>
            </Row>
            <Row>
              <p>
                {!isEmpty(state.dataContent.userPm)
                  ? `${state.dataContent.userPm.name} ${state.dataContent.userPm.surname}`
                  : "-"}
              </p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.IDclient")}</label>
            </Row>
            <Row>
              <p>{!isEmpty(state.dataContent.client) ? state.dataContent.client.id : "-"}</p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.clientName")}</label>
            </Row>
            <Row>
              <p>{!isEmpty(state.dataContent.client) ? state.dataContent.client.name : "-"}</p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.status")}</label>
            </Row>
            <Row>
              <p>{!isEmpty(state.dataContent.status) ? t(toLower(state.dataContent.status)) : "-"}</p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.dateStart")}</label>
            </Row>
            <Row>
              <p>
                {!isEmpty(state.dataContent.dateStart)
                  ? dayjs(state.dataContent.dateStart, dbDate).format(langDate[toLower(currentLanguage)])
                  : "-"}
              </p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.initiative")}</label>
            </Row>
            <Row>
              <p> {!isEmpty(state.dataContent.initiative) ? state.dataContent.initiative : "-"} </p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.salesforceId")}</label>
            </Row>
            <Row>
              <p> {!isEmpty(state.dataContent.salesforceId) ? state.dataContent.salesforceId : "-"} </p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.segment")}</label>
            </Row>
            <Row>
              <p>
                {!isEmpty(state.dataContent.costCentre) && !isEmpty(state.dataContent.costCentre.segment)
                  ? `${state.dataContent.costCentre.segment.id} | ${state.dataContent.costCentre.segment.name}`
                  : "-"}
              </p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.profitCenter")}</label>
            </Row>
            <Row>
              <p>
                {!isEmpty(state.dataContent.costCentre) && !isEmpty(state.dataContent.costCentre.profitCentre)
                  ? `${state.dataContent.costCentre.profitCentre.id} | ${state.dataContent.costCentre.profitCentre.name}`
                  : "-"}
              </p>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10">{t("projects.detail.currency")}</label>
            </Row>
            <Row>
              <p>{!isEmpty(state.dataContent.currency) ? state.dataContent.currency : "-"}</p>
            </Row>
          </Col>

          <Divider />

          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="preventive">
                {t("projects.detail.preventive")}
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  formikProject.setFieldValue("preventive", null);
                }}
                showSearch
                placeholder={t("projects.detail.preventive")}
                id="preventive"
                name="preventive"
                className="w-100"
                onBlur={formikProject.handleBlur("preventive")}
                onChange={formikProject.handleChange("preventive")}
                value={formikProject.values.preventive}
              >
                {state.preventive.map((item) => (
                  <Option key={item.id} value={item.id} name={item.id}>{`${item.id} | ${item.description}`}</Option>
                ))}
              </Select>
            </Row>
          </Col>

          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="support_pm">
                {t("projects.detail.userSupportPm")}*
              </label>
            </Row>
            <Row>
              <Select
                showSearch
                placeholder={t("projects.detail.userSupportPm")}
                id="support_pm"
                name="support_pm"
                className="w-100"
                onBlur={formikProject.handleBlur("support_pm")}
                onChange={formikProject.handleChange("support_pm")}
                value={formikProject.values.support_pm}
              >
                {state.supportPm.map((spm) => (
                  <Option key={spm.cid} value={spm.cid} name={spm.cid}>{`${spm.name} ${spm.surname}`}</Option>
                ))}
              </Select>
            </Row>
          </Col>

          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="preventiveClient">
                {t("projects.detail.preventiveClient")}
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  formikProject.setFieldValue("preventiveClient", null);
                }}
                showSearch
                placeholder={t("projects.detail.preventiveClient")}
                id="preventiveClient"
                name="preventiveClient"
                className="w-100"
                onBlur={formikProject.handleBlur("preventiveClient")}
                onChange={formikProject.handleChange("preventiveClient")}
                value={formikProject.values.preventiveClient}
              >
                {state.preventiveClient.map((item) => (
                  <Option key={item.id} value={item.id} name={item.id}>{`${item.id} | ${item.name}`}</Option>
                ))}
              </Select>
            </Row>
          </Col>

          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="projectMaster">
                {t("projects.detail.projectMaster")}*
              </label>
            </Row>
            <Row>
              <Select
                showSearch
                placeholder={t("projects.detail.projectMaster")}
                id="projectMaster"
                name="projectMaster"
                className="w-100"
                onBlur={formikProject.handleBlur("projectMaster")}
                onChange={formikProject.handleChange("projectMaster")}
                value={formikProject.values.projectMaster}
              >
                {state.projectMaster.map((item) => (
                  <Option key={item.id} value={item.id} name={item.id}>{`${item.id} | ${item.name}`}</Option>
                ))}
              </Select>
            </Row>
          </Col>

          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="salesOrder">
                {t("projects.detail.salesOrder")}
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  formikProject.setFieldValue("salesOrder", null);
                }}
                showSearch
                placeholder={t("projects.detail.salesOrder")}
                id="salesOrder"
                name="salesOrder"
                className="w-100"
                onBlur={formikProject.handleBlur("salesOrder")}
                onChange={formikProject.handleChange("salesOrder")}
                value={formikProject.values.salesOrder}
              >
                {state.salesOrder.map((item) => (
                  <Option key={item.id} value={item.id} name={item.id}>{`${item.id} | ${item.position}`}</Option>
                ))}
              </Select>
            </Row>
          </Col>

          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="statusSelling">
                {t("projects.detail.statusSelling")}
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  formikProject.setFieldValue("statusSelling", null);
                }}
                showSearch
                placeholder={t("projects.detail.statusSelling")}
                id="statusSelling"
                name="statusSelling"
                className="w-100"
                onBlur={formikProject.handleBlur("statusSelling")}
                onChange={formikProject.handleChange("statusSelling")}
                value={formikProject.values.statusSelling}
              >
                {state.statusSelling.map((item) => (
                  <Option key={item.id} value={item.id} name={item.id}>{`${item.id} | ${item.name}`}</Option>
                ))}
              </Select>
            </Row>
          </Col>

          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="contractType">
                {t("projects.detail.contractType")}
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  formikProject.setFieldValue("contractType", null);
                }}
                showSearch
                placeholder={t("projects.detail.contractType")}
                id="contractType"
                name="contractType"
                className="w-100"
                onBlur={formikProject.handleBlur("contractType")}
                onChange={formikProject.handleChange("contractType")}
                value={formikProject.values.contractType}
              >
                {contractType.map((item) => (
                  <Option key={item.id} value={item.id} name={item.value}>
                    {item.value}
                  </Option>
                ))}
              </Select>
            </Row>
          </Col>

          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="incomeType">
                {t("projects.detail.incomeType")}
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  formikProject.setFieldValue("incomeType", null);
                }}
                showSearch
                placeholder={t("projects.detail.incomeType")}
                id="incomeType"
                name="incomeType"
                className="w-100"
                onBlur={formikProject.handleBlur("incomeType")}
                onChange={formikProject.handleChange("incomeType")}
                value={formikProject.values.incomeType}
              >
                {incomeType.map((item) => (
                  <Option key={item.id} value={item.id} name={item.value}>
                    {item.value}
                  </Option>
                ))}
              </Select>
            </Row>
          </Col>

          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="paymentTerms">
                {t("projects.detail.paymentTerms")}
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  formikProject.setFieldValue("paymentTerms", null);
                }}
                showSearch
                placeholder={t("projects.detail.paymentTerms")}
                id="paymentTerms"
                name="paymentTerms"
                className="w-100"
                onBlur={formikProject.handleBlur("paymentTerms")}
                onChange={formikProject.handleChange("paymentTerms")}
                value={formikProject.values.paymentTerms}
              >
                {paymentTerms.map((item, i) => (
                  <Option key={i} value={item} name={item}>
                    {item}
                  </Option>
                ))}
              </Select>
            </Row>
          </Col>

          <Col lg={8} md={12} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="evaluationCriteria">
                {t("projects.detail.evaluationCriteria")}
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  formikProject.setFieldValue("evaluationCriteria", null);
                }}
                showSearch
                placeholder={t("projects.detail.evaluationCriteria")}
                id="evaluationCriteria"
                name="evaluationCriteria"
                className="w-100"
                onBlur={formikProject.handleBlur("evaluationCriteria")}
                onChange={formikProject.handleChange("evaluationCriteria")}
                value={formikProject.values.evaluationCriteria}
              >
                {evaluationCriteria.map((item) => (
                  <Option key={item.id} value={item.id} name={item.value}>
                    {item.value}
                  </Option>
                ))}
              </Select>
            </Row>
          </Col>

          <Col lg={16} md={16} xs={24} sm={18}>
            <Row>
              <Col lg={6} md={6} xs={24} sm={18}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="vat">
                    {t("projects.detail.vat")}
                  </label>
                </Row>
                <Row>
                  <Switch
                    id="vat"
                    name="vat"
                    onChange={() => {
                      formikProject.setFieldValue("vat", !formikProject.values.vat);
                    }}
                    checked={formikProject.values.vat}
                  />
                </Row>
              </Col>
              <Col lg={6} md={6} xs={24} sm={18}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="vht">
                    {t("projects.detail.vht")}
                  </label>
                </Row>
                <Row>
                  <Switch
                    id="vht"
                    name="vht"
                    onChange={() => {
                      formikProject.setFieldValue("vht", !formikProject.values.vht);
                    }}
                    checked={formikProject.values.vht}
                  />
                </Row>
              </Col>
              <Col lg={6} md={6} xs={24} sm={18}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="exportBpc">
                    {t("projects.detail.exportBPC")}
                  </label>
                </Row>
                <Row>
                  <Switch
                    id="exportBpc"
                    name="exportBpc"
                    onChange={() => {
                      formikProject.setFieldValue("exportBpc", !formikProject.values.exportBpc);
                    }}
                    checked={formikProject.values.exportBpc}
                  />
                </Row>
              </Col>
              <Col span={6}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="enabled">
                    {t("projects.detail.enabled")}
                  </label>
                </Row>
                <Row>
                  <Switch
                    id="enabled"
                    name="enabled"
                    onChange={() => {
                      formikProject.setFieldValue("enabled", !formikProject.values.enabled);
                    }}
                    checked={formikProject.values.enabled !== null ? formikProject.values.enabled : true}
                    disabled={formikProject.values.enabled !== null ? false : true}
                  />
                </Row>
              </Col>
            </Row>
          </Col>
          <Divider />
          <Col span={24}>
            <Title level={5}>{t("projects.detail.projectTaskInfo")}</Title>
          </Col>
          <Col span={24}>
            <Table
              key="projectTask"
              columns={columns}
              dataSource={state.tableContent}
              size="small"
              pagination={false}
              scroll={{ x: true }}
            />
          </Col>
        </Row>
        <Row type="flex" align="middle" justify="end" className="mt-10">
          <Button
            currentSize="large"
            isLoading={state.loading}
            isDisabled={!((formikProject.dirty || state.wbsFormDirty) && formikProject.isValid && checkWbsForm())}
            clickCallback={formikProject.submitForm}
            currentIcon={<SaveOutlined className="icon-large" />}
          />
        </Row>
      </div>
    </DetailBox>
  );
};

export default ProjectsDetail;
